import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Key } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useLogin } from '@/features/auth/hooks/useAuth'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useTimezone } from '@/hooks/useTimezone'
import { useAppType } from '@/hooks/useAppType'
import { cn } from '@/lib/utils'
import { 
    EnterpriseWebAuthnAPI, 
    authenticateEnterprisePasskey,
    getWebAuthnErrorMessage,
    isWebAuthnSupported 
} from '@/lib/security/webauthn-enterprise'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { useNavigate } from 'react-router-dom'
import { authApi } from '@/lib/api/clients/auth'

type LoginStep = 'email' | 'password' | 'passkey'

export function LoginPage() {
    const { t } = useTranslation()
    const { appType, isLoading: appTypeLoading } = useAppType()
    const [step, setStep] = useState<LoginStep>('email')
    
    // Debug: Log when appType changes
    useEffect(() => {
        console.log('🔍 LoginPage appType:', appType, 'isLoading:', appTypeLoading)
        console.log('🔍 Translation key:', `app.name.${appType}`, 'Translation:', t(`app.name.${appType}`))
    }, [appType, appTypeLoading, t])
    const [email, setEmail] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isWebAuthnLoading, setIsWebAuthnLoading] = useState(false)
    const [isCheckingMfa, setIsCheckingMfa] = useState(false)
    const [mfaConfig, setMfaConfig] = useState<{
        requiresPassword: boolean
        mfaMethods: string[]
        passwordlessAvailable: boolean
        webauthnNeedsSetup: boolean
        totpNeedsSetup: boolean
    } | null>(null)
    const { timezoneInfo } = useTimezone()
    const login = useLogin()
    const { toast } = useToast()
    const { setUser, setTokens, clearMfa } = useAuthStore()
    const navigate = useNavigate()
    const api = new EnterpriseWebAuthnAPI()

    const emailSchema = z.object({
        email: z.string().email(t('auth.loginPage.invalidEmail')),
    })

    const passwordSchema = z.object({
        password: z.string().min(1, t('auth.loginPage.passwordRequired')),
    })

    type EmailFormData = z.infer<typeof emailSchema>
    type PasswordFormData = z.infer<typeof passwordSchema>

    const emailForm = useForm<EmailFormData>({
        resolver: zodResolver(emailSchema),
    })

    const passwordForm = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
    })

    // Step 1: Check MFA configuration after email entry
    const handleEmailSubmit = async (data: EmailFormData) => {
        setIsCheckingMfa(true)
        setEmail(data.email)
        
        try {
            const response = await authApi.checkMfa(data.email)
            const config = response.data?.data || response.data
            
            setMfaConfig(config)
            
            if (config.passwordlessAvailable) {
                // Passwordless passkey flow - skip password
                // Show the passkey screen and automatically trigger authentication
                setStep('passkey')
                // Automatically trigger passkey authentication
                setTimeout(() => {
                    handlePasskeyAuth(data.email)
                }, 100) // Small delay to ensure UI updates first
            } else if (config.requiresPassword) {
                // Password required - show password field
                setStep('password')
            } else {
                // No MFA - proceed with normal login
                toast({
                    title: 'Error',
                    description: 'Invalid authentication configuration',
                    variant: 'destructive',
                })
            }
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to check authentication configuration',
                variant: 'destructive',
            })
        } finally {
            setIsCheckingMfa(false)
        }
    }

    // Step 2: Handle password submission
    const handlePasswordSubmit = async (data: PasswordFormData) => {
        // Proceed with normal login flow (password + MFA if needed)
        login.mutate({
            email,
            password: data.password,
            timezone: timezoneInfo?.timezone || 'UTC'
        })
    }

    // Step 3: Handle passwordless passkey authentication
    const handlePasskeyAuth = async (userEmail: string) => {
        if (!isWebAuthnSupported()) {
            toast({
                title: 'WebAuthn Not Supported',
                description: 'Your browser does not support passkeys. Please use a modern browser.',
                variant: 'destructive',
            })
            return
        }

        setIsWebAuthnLoading(true)

        try {
            const options = await api.beginAuthentication(userEmail)
            const credential = await authenticateEnterprisePasskey(options)
            const authResult = await api.completeAuthentication(credential, userEmail)
            
            const responseData = authResult.data || authResult
            if (responseData?.accessToken && responseData?.refreshToken) {
                setTokens(responseData.accessToken, responseData.refreshToken, 15 * 60)
                setUser(responseData.user)
                clearMfa()
                navigate('/')
                toast({
                    title: 'Login Successful',
                    description: 'You have been authenticated with your passkey.',
                })
            } else {
                throw new Error('Authentication failed: No tokens received')
            }
        } catch (error: any) {
            toast({
                title: 'Passkey Authentication Failed',
                description: getWebAuthnErrorMessage(error),
                variant: 'destructive',
            })
            // Reset to email step on failure
            setStep('email')
        } finally {
            setIsWebAuthnLoading(false)
        }
    }

    const handleBack = () => {
        setStep('email')
        setMfaConfig(null)
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                {/* Language selector */}
                <div className="flex justify-end">
                    <LanguageSwitcher />
                </div>

                {/* Logo and title */}
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <Logo className="h-32" />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold">
                            {appTypeLoading ? '...' : t(`app.name.${appType}`)}
                        </h1>
                        <p className="text-muted-foreground">
                            {t('auth.loginPage.title')}
                        </p>
                    </div>
                </div>

                {/* Login form - Step 1: Email */}
                {step === 'email' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('auth.loginPage.signIn')}</CardTitle>
                            <CardDescription>
                                Enter your email to continue
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth.loginPage.emailLabel')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder={t('auth.loginPage.emailPlaceholder')}
                                        {...emailForm.register('email')}
                                        className={cn(emailForm.formState.errors.email && 'border-destructive')}
                                        autoFocus
                                    />
                                    {emailForm.formState.errors.email && (
                                        <p className="text-sm text-destructive">{emailForm.formState.errors.email.message}</p>
                                    )}
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={isCheckingMfa}
                                >
                                    {isCheckingMfa ? t('auth.loginPage.checking') : t('auth.loginPage.continue')}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {/* Login form - Step 2: Password (if required) */}
                {step === 'password' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Enter Password</CardTitle>
                            <CardDescription>
                                Enter your password to continue
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email-display">Email</Label>
                                    <Input
                                        id="email-display"
                                        type="email"
                                        value={email}
                                        disabled
                                        className="bg-muted"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">{t('auth.loginPage.passwordLabel')}</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={t('auth.loginPage.passwordPlaceholder')}
                                            {...passwordForm.register('password')}
                                            className={cn(passwordForm.formState.errors.password && 'border-destructive')}
                                            autoFocus
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    {passwordForm.formState.errors.password && (
                                        <p className="text-sm text-destructive">{passwordForm.formState.errors.password.message}</p>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleBack}
                                        className="flex-1"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="flex-1"
                                        disabled={login.isPending}
                                    >
                                        {login.isPending ? t('auth.loginPage.signingIn') : t('auth.loginPage.signInButton')}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {/* Login form - Step 3: Passkey (passwordless) */}
                {step === 'passkey' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Use Passkey</CardTitle>
                            <CardDescription>
                                Sign in with your passkey - no password required
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email-display-passkey">Email</Label>
                                <Input
                                    id="email-display-passkey"
                                    type="email"
                                    value={email}
                                    disabled
                                    className="bg-muted"
                                />
                            </div>

                            <div className="text-center space-y-4 py-4">
                                <div className="flex justify-center">
                                    <Key className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div className="space-y-2">
                                    <p className="text-base font-medium">
                                        Use your passkey to sign in
                                    </p>
                                    {isWebAuthnLoading ? (
                                        <p className="text-sm text-muted-foreground animate-pulse">
                                            Waiting for passkey...
                                        </p>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            Your browser will prompt you to use your passkey
                                        </p>
                                    )}
                                </div>
                            </div>

                            {!isWebAuthnLoading && (
                                <Button
                                    type="button"
                                    onClick={() => handlePasskeyAuth(email)}
                                    className="w-full"
                                    size="lg"
                                >
                                    <Key className="mr-2 h-4 w-4" />
                                    Use Passkey
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Footer */}
                <div className="text-center text-sm text-muted-foreground">
                    <p>
                        Environment: {import.meta.env.VITE_ENVIRONMENT || 'dev'}
                    </p>
                </div>
            </div>
        </div>
    )
}
